<template>
    <aside id="elle-zip-code" ref="$zipCode" class="elle-zip-code" :class="{ show: isShow }">
        <div ref="$overflow" class="elle-zip-code--overflow" @click="showWidget = false" />
        <div ref="$wrapper" class="elle-zip-code--wrapper">
            <div class="widget-header">
                <div class="widget-header-title">
                    <span class="headline h3">{{ $t('postcode.postcode_headline') }}</span>
                    <span class="buttons">
                        <button type="button" class="widget-close" :title="$t('site.label_close')" @click="showWidget = false">
                            <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect class="vert" x="15.75" width="1.5" height="33" fill="black" />
                                <rect class="horiz" y="17.2489" width="1.5" height="33" transform="rotate(-90 0 17.2489)" fill="black" />
                            </svg>
                        </button>
                    </span>
                </div>
                <div class="widget-header-content">
                    <div class="typography">
                        <p>
                            {{ $t('postcode.postcode_status_0') }}
                        </p>
                    </div>

                    <div class="form-wrapper">
                        <Form
                            ref="$zipCodeForm"
                            class="elle-zipcode-form"
                            enctype="multipart/form-data"
                            method="POST"
                            accept-charset="UTF-8"
                            autocomplete="off"
                            :validation-schema="schema"
                            :initial-values="zipCodeFormValues"
                            @submit="onSubmitForm"
                            @invalid-submit="onInvalidSubmitForm"
                        >
                            <div class="row">
                                <div class="field-box">
                                    <div class="field">
                                        <Field
                                            id="c-zipcode"
                                            name="c-zipcode"
                                            type="number"
                                            class="c-field"
                                            autocomplete="off"
                                            :placeholder="$t('postcode.input_placeholder')"
                                            @blur="formInputBlur"
                                            @focus="formInputFocus"
                                            @change="formObjectOnChange"
                                        />
                                    </div>
                                    <div class="field">
                                        <button class="c-submit" type="submit" :title="$t('postcode.postcode_submit')">
                                            <span class="icon">
                                                <svg viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path
                                                        d="M11.4 11.1801L10.06 10.8801C10.34 8.80007 11.54 7.06007 13.1 6.58007H0.5V5.42007H13.1C11.54 4.94007 10.34 3.20007 10.06 1.12007L11.4 0.820068C11.58 3.72007 13.36 5.22007 15.5 5.42007V6.58007C13.36 6.78007 11.58 8.28007 11.4 11.1801Z"
                                                        fill="var(--btn-color)"
                                                    />
                                                </svg>
                                            </span>
                                        </button>
                                    </div>
                                </div>
                                <ErrorMessage name="c-zipcode" />
                            </div>
                        </Form>
                    </div>
                </div>
            </div>

            <div class="widget-content">
                <div v-show="statusMsg" class="widget-message">
                    <div v-show="status != 0" class="typography">
                        <p>
                            {{ statusMsg }}
                        </p>
                    </div>
                    <div v-show="status == 3" class="buttons">
                        <UiLink v-for="(link, index) in settings[0]?.postcodesButtons" :key="index" :link="link" :style="'transparent w-border'" />
                    </div>
                </div>

                <div v-show="!!locations.length" class="widget-locations">
                    <CardsLocation v-for="location in locations" :key="location.id" :location="location" :classes="['xs']" />
                </div>
            </div>
        </div>
    </aside>
</template>

<script setup lang="ts">
import { Form, Field, ErrorMessage } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { CardsLocation, UiLink } from '#components';
import type { CmsItem } from '~/types/cms';

const { t } = useI18n();
const { $gsap: gsap } = useNuxtApp();

const settingsData = useSettingsData();
const showWidget = useShowPostCodeWidget();

const $zipCode = ref<HTMLElement | null>(null);
const $zipCodeForm = ref<{ resetForm: () => void } | null>(null);
const $overflow = ref<HTMLDivElement | null>(null);
const $wrapper = ref<HTMLDivElement | null>(null);

const isShow = ref(false);
const status = ref(0);
const statusMsg = ref<string | null>(null);
const locations = ref<CmsItem[]>([]);

const animationTimeline = ref<any>(null);

const settings = computed<CmsItem[]>(() => settingsData.value.filter(item => item.siteId == useSiteId()));

const zipCodeFormValues = ref({
    'c-zipcode': ''
});

const zodObj = z.object({
    'c-zipcode': z
        .number({
            required_error: t('postcode.postcode_required'),
            invalid_type_error: t('postcode.postcode_digits')
        })
        .min(1000, {
            message: t('postcode.postcode_min_max')
        })
        .max(9999, {
            message: t('postcode.postcode_min_max')
        })
});

const schema = toTypedSchema(zodObj);

type ZipCodeValues = Record<string, string | number | null | undefined>;
type InvalidSubmitResult = { valid: boolean };
type ZipCodeField = HTMLInputElement;

const onSubmitForm = async (values: ZipCodeValues) => {
    const postcodes = settings.value[0]?.postcodes ?? [];

    try {
        const zCodeValue = parseInt(String(values['c-zipcode'] ?? ''), 10);
        const currentItem = postcodes.find((x: CmsItem) => x.postcode.find((y: CmsItem) => y.postcode == zCodeValue));

        if (currentItem !== undefined) {
            const currentZipCode = currentItem.postcode.find((y: CmsItem) => y.postcode == zCodeValue);
            if (!currentZipCode.notSure) {
                status.value = 1;
            } else {
                status.value = 2;
            }

            locations.value = currentItem.locations ?? [];
        } else {
            status.value = 3;
            locations.value = [];
        }
        statusMsg.value = t(`postcode.postcode_status_${status.value}`);
    } catch (error) {
        console.log(error);
    } finally {
        //
    }
};

const onInvalidSubmitForm = async ({ results }: { results: Partial<Record<string, InvalidSubmitResult>> }) => {
    for (const [key, value] of Object.entries(results)) {
        const el = document.getElementById(key);

        if (el && value) {
            if (!value.valid) {
                el.focus();
                el.parentElement?.classList.add('hasError');
            } else {
                el.parentElement?.classList.remove('hasError');
            }
        }
    }
};
const formInputFocus = (e: Event) => (e.target as ZipCodeField).parentElement?.classList.add('focused');
const formInputBlur = (e: Event) => {
    const target = e.target as ZipCodeField;
    if (target.value == '') {
        target.classList.remove('filled');
        target.parentElement?.classList.remove('focused');
    } else {
        target.classList.add('filled');
    }
};
const formObjectOnChange = (e: Event) => {
    const target = e.target as ZipCodeField;
    return target.value != '' ? target.classList.add('hasValue') : target.classList.remove('hasValue');
};

useStartAnimations($zipCode.value, () => {
    if (!$overflow.value || !$wrapper.value) return;

    gsap.set($overflow.value, {
        xPercent: 100
    });
    gsap.set($wrapper.value, {
        xPercent: 100
    });

    animationTimeline.value = gsap
        .timeline({
            paused: true,
            onReverseComplete: () => {
                isShow.value = false;
                status.value = 0;
                $zipCodeForm.value?.resetForm();
                statusMsg.value = null;
                locations.value = [];
            }
        })
        .to($overflow.value, {
            xPercent: 0,
            duration: 0.2,
            ease: 'power2.inOut'
        })

        .to($wrapper.value, {
            xPercent: 0,
            duration: 0.5,
            ease: 'power4.inOut'
        });
});

watch(
    () => showWidget.value,
    state => {
        if (state) {
            isShow.value = true;
            animationTimeline.value?.play();
        } else {
            animationTimeline.value?.reverse();
        }
    }
);
</script>
